import { useApolloClient } from '@apollo/client';
import type { AssetClassLevel } from '@generated/graphql';
import {
  ASSET_CLASS_FIELD_BY_LEVEL,
  type FinancialProduct,
} from '@interfaces/financialProduct';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { UserContext } from '@providers/userContextProvider';
import { Box } from 'hephaestus/web';
import { useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';
import { usePermission } from '../../hooks/usePermission';
import { SEARCH_SECURITIES } from './securitySearchSelect.query';

const PAGE_SIZE = 50;

type SearchCursor = {
  query: string;
  loaded: number;
  total: number;
};

export type SecuritySearchSelectProp = {
  value: FinancialProduct | null;
  onChange: (security: FinancialProduct) => void;
  /** Products already in the model, left out of the results. */
  excludedProductIds?: string[];
  /** Shows each option's asset class at this level (asset-class models). */
  assetClassLevel?: AssetClassLevel | string;
  label?: string;
  size?: 'small' | 'medium';
  autoFocus?: boolean;
  disabled?: boolean;
  testId?: string;
};

/**
 * Picks one security from the whole product shelf, searched on the server.
 * The current value is shown from the row itself, so it never renders blank
 * when it sits outside the loaded results.
 */
export const SecuritySearchSelect = ({
  value,
  onChange,
  excludedProductIds = [],
  assetClassLevel,
  label = '',
  size,
  autoFocus,
  disabled,
  testId = 'security-search-select',
}: SecuritySearchSelectProp) => {
  const { t } = useTranslation(['components']);
  const apolloClient = useApolloClient();
  const { activeOrganization } = useContext(UserContext);
  const { translateBackendString } = useLocalization();
  const canReadFinancialProducts = usePermission('read:financial_products');
  const [cursor, setCursor] = useState<SearchCursor | null>(null);
  const [searchFailed, setSearchFailed] = useState(false);
  // Ref mirrors `cursor` so the paging callbacks stay stable for Autocomplete.
  const cursorRef = useRef<SearchCursor>({ query: '', loaded: 0, total: 0 });
  // A fetch that outlives a newer search must not overwrite the cursor.
  const searchSeq = useRef(0);

  const assetClassField = assetClassLevel
    ? ASSET_CLASS_FIELD_BY_LEVEL[assetClassLevel as AssetClassLevel]
    : undefined;

  const optionLabel = (option: FinancialProduct) => {
    const name =
      translateBackendString(option.translatedName) || option.name || '';
    return option.ticker ? `${option.ticker} - ${name}` : name;
  };

  const fetchPage = useCallback(
    async (searchText: string, offset: number) => {
      const { data } = await apolloClient.query({
        query: SEARCH_SECURITIES,
        variables: {
          input: {
            filter: {
              organizationId: activeOrganization.id,
              searchText: searchText || undefined,
            },
            pagination: { offset, perPage: PAGE_SIZE, sortField: 'ticker' },
          },
        },
        fetchPolicy: 'no-cache',
      });
      // The model table's rows are typed with the legacy FinancialProduct shape.
      const products = (data?.fetchFinancialProducts.financialProducts ??
        []) as FinancialProduct[];
      return {
        products: products.filter(
          (product) =>
            product.id === value?.id ||
            !excludedProductIds.includes(product.id ?? ''),
        ),
        fetched: products.length,
        total: data?.fetchFinancialProducts.totalCount ?? 0,
      };
    },
    [apolloClient, activeOrganization.id, excludedProductIds, value?.id],
  );

  const storeCursor = useCallback((next: SearchCursor) => {
    cursorRef.current = next;
    setCursor(next);
  }, []);

  const searchSecurities = useCallback(
    async (searchText: string) => {
      const seq = ++searchSeq.current;
      const { products, fetched, total } = await fetchPage(searchText, 0);
      if (seq !== searchSeq.current) return [];
      storeCursor({ query: searchText, loaded: fetched, total });
      setSearchFailed(false);
      return products;
    },
    [fetchPage, storeCursor],
  );

  const searchMoreSecurities = useCallback(async () => {
    const current = cursorRef.current;
    if (current.loaded >= current.total) return [];
    const seq = ++searchSeq.current;
    const { products, fetched, total } = await fetchPage(
      current.query,
      current.loaded,
    );
    if (seq !== searchSeq.current) return [];
    storeCursor({
      query: current.query,
      loaded: current.loaded + fetched,
      total,
    });
    return products;
  }, [fetchPage, storeCursor]);

  if (!canReadFinancialProducts) return null;

  return (
    <Autocomplete<FinancialProduct>
      testId={testId}
      label={label}
      size={size}
      autoFocus={autoFocus}
      disabled={disabled}
      showSearchIcon
      fullWidth
      disableClearable
      value={value}
      isOptionEqualToValue={(
        option: FinancialProduct,
        selected: FinancialProduct,
      ) => option.id === selected.id}
      getOptionLabel={(option: FinancialProduct | null) =>
        option ? optionLabel(option) : ''
      }
      renderOption={(props: object, option: FinancialProduct) => (
        <MenuItem
          {...props}
          key={option.id}
          data-testid={`${testId}-${option.id}`}
        >
          {option.ticker && (
            <>
              <strong>{option.ticker}</strong>
              &nbsp;-&nbsp;
            </>
          )}
          {translateBackendString(option.translatedName) || option.name}
          {assetClassField && option[assetClassField] && (
            <Box component='span' sx={{ ml: 1, opacity: 0.6 }}>
              {translateBackendString(
                option[assetClassField]?.translatedName,
              ) || option[assetClassField]?.name}
            </Box>
          )}
        </MenuItem>
      )}
      onChange={(_event: unknown, next: FinancialProduct | null) => {
        if (next) onChange(next);
      }}
      onSearch={searchSecurities}
      onSearchMore={searchMoreSecurities}
      hasMore={cursor !== null && cursor.total > cursor.loaded}
      listFooterTestId={`${testId}-loading-more`}
      onSearchError={() => setSearchFailed(true)}
      error={searchFailed}
      errorText={
        searchFailed
          ? t('components:securitySearchSelect.searchFailed')
          : undefined
      }
      noOptionsText={t('components:securitySearchSelect.emptyList')}
    />
  );
};
